import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Scale, ArrowLeft, Building2, User as UserIcon, Calendar, AlertTriangle,
  Loader2, Trash2, FileDown, FolderPlus, RefreshCw, ExternalLink, ScanLine,
  CheckCircle2, XCircle, ShieldAlert, Gavel, FileText, Sparkles,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ExpedienteSelector } from '../components/ExpedienteSelector';
import { BANDA_COLOR } from './AnalisisCAHipotecaria';
import { toast } from '../hooks/use-toast';
import { obtenerToken } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;

const formatEur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v || 0);
const tokenHeader = () => ({ headers: { Authorization: `Bearer ${obtenerToken()}` } });

const RES_COLOR = {
  SUPERA: 'bg-green-100 text-green-800 border-green-300',
  NO_SUPERA: 'bg-red-100 text-red-800 border-red-300',
  INDETERMINADO: 'bg-yellow-100 text-yellow-800 border-yellow-300',
};

const TEST_LABEL = {
  T1_requisitos_sectoriales: 'T1 · Requisitos sectoriales',
  T2_solapamiento: 'T2 · Solapamiento de comisiones',
  T3_devengo: 'T3 · Devengo',
  T4_transparencia_material: 'T4 · Transparencia material e información precontractual',
  T5_proporcionalidad: 'T5 · Proporcionalidad del importe',
};

async function descargarArchivo(url, suggestedName) {
  let res;
  try {
    res = await axios.get(url, { ...tokenHeader(), responseType: 'blob' });
  } catch (err) {
    // El cuerpo de error llega como Blob: lo leemos para mostrar el detalle real
    const data = err.response?.data;
    if (data instanceof Blob) {
      try {
        const txt = await data.text();
        const parsed = JSON.parse(txt);
        err.message = parsed.detail || txt;
      } catch (_) { /* cuerpo no JSON */ }
    }
    throw err;
  }
  const link = document.createElement('a');
  link.href = URL.createObjectURL(res.data);
  const cd = res.headers['content-disposition'] || '';
  const m = cd.match(/filename="([^"]+)"/);
  link.download = m ? m[1] : suggestedName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

const Dato = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-medium text-gray-900 break-words">{value || 'NO CONSTA'}</p>
  </div>
);

export default function AnalisisCADetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [expediente, setExpediente] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/hipotecas/analisis/${id}`, tokenHeader());
      setA(r.data);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo cargar el análisis', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!a || !['analizando', 'convirtiendo'].includes(a.estado)) return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [a, load]);

  const accion = async (fn, label) => {
    setBusy(label);
    try {
      await fn();
      await load();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const exportar = (formato) => accion(async () => {
    await descargarArchivo(`${API}/api/hipotecas/analisis/${id}/export/${formato}`, `informe_ca_${id}.${formato}`);
    toast({ title: `Informe ${formato.toUpperCase()} descargado` });
  }, formato);

  const vincular = () => accion(async () => {
    if (!expediente) return;
    await axios.post(`${API}/api/hipotecas/analisis/${id}/vincular-expediente`, { expediente_id: expediente.id }, tokenHeader());
    toast({ title: 'Asociado al expediente', description: 'Se ha creado la actuación con el informe PDF adjunto.' });
    setExpediente(null);
  }, 'vincular');
  const crearExpediente = () => accion(async () => {
    const r = await axios.post(`${API}/api/hipotecas/analisis/${id}/crear-expediente`, {}, tokenHeader());
    toast({ title: 'Expediente creado', description: r.data.numero_expediente });
  }, 'crear-exp');

  const crearActuacion = () => accion(async () => {
    const r = await axios.post(`${API}/api/hipotecas/analisis/${id}/crear-actuacion`, {}, tokenHeader());
    toast({
      title: 'Actuación creada',
      description: `Añadida al expediente con el informe PDF adjunto (actuación #${r.data.actuacion_id}).`,
    });
  }, 'actuacion');

  const relanzar = () => accion(async () => {
    await axios.post(`${API}/api/hipotecas/analisis/${id}/analizar`, {}, tokenHeader());
    toast({ title: 'Análisis relanzado' });
  }, 'analizar');

  const reintentarOcr = () => accion(async () => {
    await axios.post(`${API}/api/hipotecas/analisis/${id}/reintentar-ocr`, {}, tokenHeader());
    toast({ title: 'OCR relanzado' });
  }, 'ocr');

  const borrar = () => accion(async () => {
    if (!window.confirm('¿Borrar este análisis?')) return;
    await axios.delete(`${API}/api/hipotecas/analisis/${id}`, tokenHeader());
    navigate('/expedientes/analisis-ca-hipotecaria');
  }, 'borrar');

  const verOriginal = () => accion(async () => {
    const r = await axios.get(`${API}/api/hipotecas/analisis/${id}/archivo-original?as=url`, tokenHeader());
    window.open(r.data.url, '_blank');
  }, 'original');

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  }
  if (!a) return <div className="p-6">Análisis no encontrado</div>;

  const R = a.resultado_json || {};
  const op = R.operacion || {};
  const cl = R.clausula || {};
  const calc = R.calculos || {};
  const punt = R.puntuacion || {};
  const cuant = R.cuantificacion || {};
  const presc = R.prescripcion || {};
  const rec = R.recomendacion || {};
  const analizado = a.estado === 'analizado' || !!a.informe_markdown;

  return (
    <div className="p-6 space-y-6" data-testid="analisis-ca-detalle-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/expedientes/analisis-ca-hipotecaria')} data-testid="btn-volver">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Scale className="w-6 h-6 text-indigo-600" />
              Comisión de apertura — análisis #{a.id}
            </h1>
            <p className="text-sm text-gray-600 mt-1 break-all">{a.nombre_archivo_original}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {a.viabilidad_banda && (
            <Badge className={`${BANDA_COLOR[a.viabilidad_banda] || 'bg-gray-500 text-white'} text-sm px-3 py-1`} data-testid="badge-viabilidad">
              Viabilidad {a.viabilidad_banda.replace('_', ' ')} · {a.puntuacion_total} pts
            </Badge>
          )}
          {a.metodo_ocr && (
            <Badge variant="outline" data-testid="badge-ocr">
              <ScanLine className="w-3 h-3 mr-1" /> OCR {a.metodo_ocr === 'ia' ? 'por IA' : 'local'}
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="btn-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {['analizando', 'convirtiendo'].includes(a.estado) && (
        <Card className="border-indigo-300 bg-indigo-50">
          <CardContent className="p-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
            <div>
              <p className="font-medium text-indigo-900">
                {a.estado === 'convirtiendo' ? 'Preparando el documento (conversión / OCR)…' : 'La IA está analizando la comisión de apertura…'}
              </p>
              <p className="text-sm text-indigo-700">{a.paso_actual || 'Esto puede tardar 1-3 minutos.'}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {a.error_mensaje && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-4 flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-red-900">Incidencia</p>
              <p className="text-sm text-red-700">{a.error_mensaje}</p>
            </div>
            {a.estado === 'sin_ocr' && (
              <Button size="sm" variant="outline" onClick={reintentarOcr} disabled={busy === 'ocr'} data-testid="btn-reintentar-ocr">
                {busy === 'ocr' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4 mr-1" />} Reintentar OCR
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {(a.avisos_validacion || []).length > 0 && (
        <Card className="border-amber-300 bg-amber-50" data-testid="avisos-validacion">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-900">
              <ShieldAlert className="w-5 h-5" /> Control de calidad — revisión humana recomendada
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="list-disc pl-5 space-y-1 text-sm text-amber-900">
              {a.avisos_validacion.map((av, i) => <li key={i}>{av}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Acciones */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          {a.estado === 'convertido' && (
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={relanzar} disabled={busy === 'analizar'} data-testid="btn-analizar">
              <Sparkles className="w-4 h-4 mr-2" /> Analizar con IA
            </Button>
          )}
          {analizado && (
            <>
              <Button variant="outline" onClick={() => exportar('pdf')} disabled={busy === 'pdf'} data-testid="btn-export-pdf">
                {busy === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} PDF
              </Button>
              <Button variant="outline" onClick={() => exportar('docx')} disabled={busy === 'docx'} data-testid="btn-export-docx">
                {busy === 'docx' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} DOCX
              </Button>
              <Button variant="outline" onClick={() => exportar('md')} disabled={busy === 'md'} data-testid="btn-export-md">
                {busy === 'md' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} MD
              </Button>
            </>
          )}
          <Button variant="outline" onClick={verOriginal} disabled={busy === 'original'} data-testid="btn-ver-original">
            <ExternalLink className="w-4 h-4 mr-2" /> Documento original
          </Button>

          <div className="h-10 w-px bg-gray-200 hidden md:block" />

          {a.expediente_id ? (
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">Expediente asociado</p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => window.open(`/expedientes/${a.expediente_id}`, '_blank', 'noopener')}
                    data-testid="btn-ir-expediente"
                  >
                    <FolderPlus className="w-4 h-4 mr-2" />
                    {a.expediente_numero || `#${a.expediente_id}`}
                    <ExternalLink className="w-3.5 h-3.5 ml-2 opacity-60" />
                  </Button>
                  {a.actuacion_id ? (
                    <Badge className="bg-green-100 text-green-800 h-10 px-3 flex items-center gap-1" data-testid="badge-actuacion-creada">
                      <CheckCircle2 className="w-4 h-4" />
                      Actuación #{a.actuacion_id} creada con el informe PDF
                    </Badge>
                  ) : analizado ? (
                    <Button onClick={crearActuacion} disabled={busy === 'actuacion'} data-testid="btn-crear-actuacion">
                      {busy === 'actuacion' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-2" />}
                      Crear actuación en el expediente
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">Asociar a expediente y crear actuación</p>
                <ExpedienteSelector value={expediente} onChange={setExpediente} testId="detalle-expediente" />
              </div>
              <Button onClick={vincular} disabled={!expediente || busy === 'vincular'} data-testid="btn-vincular-expediente">
                {busy === 'vincular' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-2" />} Asociar
              </Button>
              {analizado && (
                <Button variant="outline" onClick={crearExpediente} disabled={busy === 'crear-exp'} data-testid="btn-crear-expediente">
                  Crear expediente nuevo
                </Button>
              )}
            </div>
          )}

          <Button variant="ghost" className="ml-auto" onClick={borrar} data-testid="btn-borrar">
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </CardContent>
      </Card>

      {analizado && (
        <Tabs defaultValue="resumen">
          <TabsList>
            <TabsTrigger value="resumen" data-testid="tab-resumen">Resumen</TabsTrigger>
            <TabsTrigger value="tests" data-testid="tab-tests">Controles y puntuación</TabsTrigger>
            <TabsTrigger value="informe" data-testid="tab-informe">Informe</TabsTrigger>
            <TabsTrigger value="json" data-testid="tab-json">Datos brutos</TabsTrigger>
          </TabsList>

          <TabsContent value="resumen" className="space-y-4 mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">La operación</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Dato label="Naturaleza" value={op.naturaleza} />
                <Dato label="Fecha de otorgamiento" value={op.fecha_otorgamiento} />
                <Dato label="Entidad" value={op.entidad_original} />
                <Dato label="Entidad actual" value={op.entidad_actual} />
                <Dato label="Notario" value={op.notario} />
                <Dato label="Protocolo" value={op.protocolo} />
                <Dato label="Capital o límite" value={op.capital_o_limite ? formatEur(op.capital_o_limite) : null} />
                <Dato label="1ª disposición" value={op.importe_primera_disposicion ? formatEur(op.importe_primera_disposicion) : null} />
                <Dato label="Vencimiento" value={op.fecha_vencimiento} />
                <Dato label="Crédito vigente" value={op.credito_vigente === undefined ? null : (op.credito_vigente ? 'Sí' : 'No')} />
                <Dato label="Tramo normativo" value={op.tramo_normativo} />
                <Dato label="Ámbito Orden 1994" value={op.ambito_orden_1994} />
                <div className="col-span-2 md:col-span-4">
                  <p className="text-xs text-gray-500">Norma aplicable</p>
                  <p className="text-sm font-medium">{op.norma_aplicable || 'NO CONSTA'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Condición de consumidor</p>
                  <Badge className={(op.condicion_consumidor || '').toUpperCase() === 'SI' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {op.condicion_consumidor || 'NO CONSTA'}
                  </Badge>
                </div>
                <div className="col-span-2 md:col-span-3">
                  <p className="text-xs text-gray-500">Prestatarios</p>
                  <p className="text-sm font-medium">{(op.prestatarios || []).join(', ') || 'NO CONSTA'}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">La cláusula controvertida</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">{cl.ubicacion || 'Ubicación NO CONSTA'}</Badge>
                  <Badge variant="outline">Idioma: {cl.idioma_original || '—'}</Badge>
                  <Badge className={cl.expresa_base_de_calculo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {cl.expresa_base_de_calculo ? 'Expresa la base de cálculo' : 'Base de cálculo indeterminada'}
                  </Badge>
                  <Badge className={cl.resaltada_tipograficamente ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}>
                    {cl.resaltada_tipograficamente ? 'Resaltada tipográficamente' : 'Sin resalte'}
                  </Badge>
                  <Badge variant="outline">TAE: {cl.incluida_en_tae || 'NO CONSTA'}</Badge>
                </div>
                <blockquote className="border-l-4 border-indigo-400 bg-gray-50 p-3 text-sm whitespace-pre-wrap" data-testid="transcripcion-clausula">
                  {cl.transcripcion_literal || 'NO CONSTA EN LA DOCUMENTACIÓN APORTADA'}
                </blockquote>
                {(R.comisiones_conexas || []).length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-2">Comisiones conexas</p>
                    <div className="space-y-2">
                      {R.comisiones_conexas.map((c, i) => (
                        <div key={i} className="border rounded p-2 text-sm">
                          <div className="flex flex-wrap gap-2 items-center">
                            <span className="font-medium">{c.denominacion}</span>
                            <Badge variant="outline">{c.importe_o_tipo}</Badge>
                            {c.retribuye_mismo_concepto && <Badge className="bg-red-100 text-red-800">Mismo concepto → solapamiento</Badge>}
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{c.ubicacion}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Cuantificación</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">Principal reclamable</span><span className="font-semibold">{formatEur(cuant.principal_reclamable)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Total estimado</span><span className="font-semibold text-emerald-700">{formatEur(cuant.total_estimado)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Intereses</span><span>{cuant.intereses || 'a liquidar'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Prueba del pago</span><span>{cuant.prueba_del_pago || 'NO CONSTA'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Procedimiento</span><span>{cuant.tipo_procedimiento || '—'}</span></div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Prescripción y recomendación</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Riesgo de prescripción:</span>
                    <Badge className={presc.riesgo === 'ALTO' ? 'bg-red-100 text-red-800' : presc.riesgo === 'MEDIO' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>
                      {presc.riesgo || '—'}
                    </Badge>
                  </div>
                  <p className="text-gray-700"><strong>Dies a quo:</strong> {presc.dies_a_quo_defendido || '—'}</p>
                  <div className="pt-2 border-t">
                    <Badge className="bg-indigo-600 text-white mb-2">{rec.accion || '—'}</Badge>
                    <p className="text-gray-700">{rec.justificacion}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {(R.defensa_previsible_entidad || []).length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Defensa previsible de la entidad y riesgos</CardTitle></CardHeader>
                <CardContent className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-semibold mb-1">Argumentos de la entidad</p>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      {R.defensa_previsible_entidad.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Riesgos y puntos débiles</p>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      {(R.riesgos_y_puntos_debiles || []).map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="tests" className="space-y-4 mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Los cinco controles (§4)</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(R.tests || {}).map(([k, v]) => (
                  <div key={k} className="border rounded-lg p-3" data-testid={`test-${k}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="font-medium text-sm">{TEST_LABEL[k] || k}</span>
                      <Badge className={RES_COLOR[v.resultado] || 'bg-gray-100 text-gray-700'}>{v.resultado || '—'}</Badge>
                    </div>
                    {v.evidencia && <p className="text-xs text-gray-600 mt-2 italic">"{v.evidencia}"</p>}
                    {v.razonamiento && <p className="text-sm text-gray-800 mt-1">{v.razonamiento}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Cálculos de proporcionalidad</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="text-gray-600">Capital de referencia: <strong>{calc.capital_de_referencia_aplicado || '—'}</strong></p>
                {['pct_primera_disposicion', 'pct_sobre_capital_total', 'pct_disposiciones_sucesivas', 'pct_efectivo_minimo'].map((k) => (
                  calc[k] && calc[k].valor !== undefined && (
                    <div key={k} className="flex justify-between border-b py-1">
                      <span className="text-gray-600">{k.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{calc[k].valor} % <span className="text-xs text-gray-500">({calc[k].operacion})</span></span>
                    </div>
                  )
                ))}
                {calc.comparacion_horquilla && (
                  <Badge className={calc.comparacion_horquilla === 'EXCEDE' ? 'bg-red-100 text-red-800' : calc.comparacion_horquilla === 'LIMITE' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>
                    Horquilla 0,25 %–1,50 %: {calc.comparacion_horquilla}
                  </Badge>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Causas de abusividad</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {(R.causas_de_abusividad || []).map((c, i) => (
                  <div key={i} className={`border rounded-lg p-3 ${c.concurre ? 'border-red-300 bg-red-50' : 'opacity-60'}`} data-testid={`causa-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      {c.concurre ? <CheckCircle2 className="w-4 h-4 text-red-600" /> : <XCircle className="w-4 h-4 text-gray-400" />}
                      <span className="font-semibold text-sm">{c.causa}</span>
                      <Badge variant="outline">Fortaleza {c.fortaleza}</Badge>
                    </div>
                    <p className="text-xs text-gray-700 mt-2"><strong>Normativa:</strong> {(c.fundamento_normativo || []).join('; ')}</p>
                    <p className="text-xs text-gray-700"><strong>Jurisprudencia:</strong> {(c.fundamento_jurisprudencial || []).join('; ')}</p>
                    {c.riesgo_casacional && <p className="text-xs text-gray-700 mt-1"><strong>Riesgo casacional:</strong> {c.riesgo_casacional}</p>}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Gavel className="w-4 h-4" /> Matriz de puntuación — {punt.total || 0} puntos
                  {punt.banda && <Badge className={BANDA_COLOR[punt.banda] || ''}>{punt.banda.replace('_', ' ')}</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {(punt.detalle || []).map((d, i) => (
                  <div key={i} className="flex justify-between border-b py-1">
                    <span className="text-gray-700">{d.indicador}</span>
                    <span className={`font-semibold ${Number(d.puntos) < 0 ? 'text-green-700' : 'text-red-700'}`}>{d.puntos > 0 ? '+' : ''}{d.puntos}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="informe" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Informe de viabilidad
                </CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="whitespace-pre-wrap text-sm font-sans text-gray-800 leading-relaxed" data-testid="informe-markdown">
                  {a.informe_markdown || 'Informe no disponible'}
                </pre>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="json" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">JSON devuelto por la IA</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xs text-gray-500 mb-2 flex flex-wrap gap-4">
                  <span>Modelo: {a.provider}/{a.model}</span>
                  <span>Tokens: {a.tokens_input} in / {a.tokens_output} out</span>
                  <span>Duración: {Math.round(a.duracion_segundos || 0)} s</span>
                </div>
                <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-auto max-h-[600px]" data-testid="json-bruto">
                  {JSON.stringify(R, null, 2)}
                </pre>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {!analizado && !['analizando', 'convirtiendo'].includes(a.estado) && (
        <Card>
          <CardContent className="p-10 text-center text-gray-500">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            Todavía no hay informe. Lanza el análisis cuando el documento esté listo.
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-gray-500 flex items-center gap-4 flex-wrap">
        <span><Calendar className="w-3 h-3 inline mr-1" />Creado {new Date(a.created_at).toLocaleString('es-ES')}</span>
        {op.entidad_original && <span><Building2 className="w-3 h-3 inline mr-1" />{op.entidad_original}</span>}
        {(op.prestatarios || []).length > 0 && <span><UserIcon className="w-3 h-3 inline mr-1" />{op.prestatarios[0]}</span>}
      </p>
    </div>
  );
}
